import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CostCenterEnums } from 'src/app/enum/cost-center-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { InvoicingCutoffModel } from 'src/app/models/invoicing/invoicing-cutoff.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoicingCutoffDetailRegisterTable } from 'src/app/models/tables/invoicing/invoicing-cutoff-detail-register-table.model';
import { InvoicingCutoffDetailService } from 'src/app/services/invoicing/invoicing-cutoff-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoicing-cutoff-detail',
  templateUrl: './invoicing-cutoff-detail.component.html',
  styleUrls: ['./invoicing-cutoff-detail.component.scss'],
  standalone: false
})
export class InvoicingCutoffDetailComponent implements OnInit {

  @Input() invoicingCutoffModel: InvoicingCutoffModel = new InvoicingCutoffModel();
  @Input() rowsEditable: boolean = true;
  @Input() updateInvoicingCutoffListDetail: boolean = true;
  @Input() visibleFilters: boolean = false;
  @Input() disabledSelectCostCenter: boolean = false;
  @Output() quantityCostCenterTypeThird = new EventEmitter<number>();
  @Output() quantityDetails = new EventEmitter<number>();
  @Output() detailWithoutMovement = new EventEmitter<boolean>();
  @Output() thereIsCostCenterWithAlerts = new EventEmitter<void>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    }
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<InvoicingCutoffDetailRegisterTable> = new PagedResponseModel<InvoicingCutoffDetailRegisterTable>();
  detailHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  filters: ColumnResponseModel[] = [];
  limit: number = 10;
  listShowView: ColumnResponseModel = new ColumnResponseModel();
  page: number = 1;
  showDeployBy: boolean = false;
  visibleListDetail: boolean = false;

  constructor(private invoicingCutoffDetailService: InvoicingCutoffDetailService, private toastHandlerService: ToastHandlerService) { }

  async ngOnInit(): Promise<void> {
    await this.getColumns();
  }

  ngOnChanges(): void {
    if (this.invoicingCutoffModel.invoicingCutoffId != 0 && this.invoicingCutoffModel.invoicingCutoffId != -1) {
      this.filters = [];
      this.clearSelectionTrigger++;
      this.getInvoicingCutoffDetailsForRegisterTable();
    } else {
      this.visibleListDetail = false;
    }
  }

  async deleteSomeDetails(ids: number[]) {
    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle seleccionado del corte de facturación?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        await firstValueFrom(this.invoicingCutoffDetailService.deleteInvoicingCutoffDetail(ids));
        this.toastHandlerService.showToast('success', 'El detalle del corte de facturación fue eliminado exitosamente.', 'Atención');
        this.clearSelectionTrigger++;
        this.getInvoicingCutoffDetailsForRegisterTable();
      } catch (error) {
        console.error('Error al eliminar el detalle del corte de facturación.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.invoicingCutoffDetailService.getColumnsForRegisterTable(this.showDeployBy));
      this.listShowView = this.detailHeader.filtersGlobal[0];
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getInvoicingCutoffDetailsForRegisterTable() {
    try {
      this.detailData = await firstValueFrom(this.invoicingCutoffDetailService.getInvoicingCutoffDetailsForRegisterTable(this.page, this.limit, this.columnSort, this.ascendant, this.showDeployBy, this.invoicingCutoffModel.invoicingCutoffId, this.filters));
      const quantityThird = this.detailData.items.filter(x => x.costCenterTypeId === CostCenterEnums.TipoTercero).length;
      this.quantityCostCenterTypeThird.emit(quantityThird);
      this.quantityDetails.emit(this.detailData.totalItems);
      if (this.detailData.totalItems != 0 || this.showDeployBy || this.filters.length != 0) this.visibleListDetail = true;
      else this.visibleListDetail = false;
      this.thereIsCostCenterWithAlerts.emit();
    } catch (error) {
      console.error('Error al buscar los detalles del corte de facturación.', error);
    }
  }

  getUpdateListDetail(updateListDetail: boolean) {
    if (updateListDetail) {
      this.getInvoicingCutoffDetailsForRegisterTable();
    }
  }

  async onAction(event: { action: string, item?: InvoicingCutoffDetailRegisterTable, selectedItems?: number[] }) {
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteSomeDetails(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
    this.getInvoicingCutoffDetailsForRegisterTable();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getInvoicingCutoffDetailsForRegisterTable();
  }

  async onShowDeployByChange(showDeployBy: boolean) {
    this.showDeployBy = showDeployBy;
    await this.getColumns();
    this.getInvoicingCutoffDetailsForRegisterTable();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter == null || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
